/**
 * ROOM FILE URLS (CONTEXT-DOCS.md §P1) — the Doc pane + card previews share ONE short-lived URL per file (the server
 * signs for 300 s; we reuse it for 240 s), so a card preview and opening the doc never sign twice. In-flight requests
 * are shared too.
 */
import { getDiscussionFileUrl } from '@stepflow/ui-shared/services/dataPlaneApi';

const REUSE_MS = 240 * 1000;
const cache = new Map();          // key → { at, promise }

export function __resetRoomFileUrls() { cache.clear(); }

/** → Promise<{ url, fileName, contentType, size }> */
export function roomFileUrl(networkId, poolId, m, index, now = Date.now) {
  const key = `${networkId}|${poolId}|${m && m.id}|${index}`;
  const hit = cache.get(key);
  if (hit && now() - hit.at < REUSE_MS) return hit.promise;
  const promise = Promise.resolve().then(() => getDiscussionFileUrl(networkId, m, index, { pool: poolId }));
  cache.set(key, { at: now(), promise });
  promise.catch(() => cache.delete(key));
  return promise;
}

/** The kind of file (drives the icon + preview). Pure. */
export function fileKind(name = '', type = '') {
  const n = String(name).toLowerCase();
  const t = String(type).toLowerCase();
  if (t.startsWith('image/') || /\.(png|jpe?g|tiff?|gif|webp)$/.test(n)) return 'image';
  if (t === 'application/pdf' || n.endsWith('.pdf')) return 'pdf';
  if (/\.(docx)$/.test(n)) return 'doc';
  if (/\.(xlsx|csv|tsv)$/.test(n)) return 'sheet';
  if (/\.(pptx)$/.test(n)) return 'slides';
  if (/\.(zip|tar|tgz|tar\.gz)$/.test(n)) return 'archive';
  if (/\.(md|markdown|txt|log|rst)$/.test(n)) return 'text';
  return 'code';
}

/** "SOW v2" + "PDF" — the card's title and type label. Pure. */
export function splitName(name = '') {
  const s = String(name);
  const lower = s.toLowerCase();
  const ext = lower.endsWith('.tar.gz') ? 'tar.gz' : (s.includes('.') ? s.split('.').pop() : '');
  const title = ext ? s.slice(0, s.length - ext.length - 1) : s;
  return { title: title || s, ext: ext.toUpperCase() };
}

export function formatBytes(n) {
  const b = Number(n) || 0;
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(b < 10 * 1024 ? 1 : 0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}
